import { useCallback } from 'react';

import {
  CustomSideType,
  DiagonalCornerOptions,
  SideItemType,
  UserDesignOperation,
} from '../enums';
import { ApplianceModel } from './../models/appliance.model';

import { useAppDispatch } from '~/redux/hooks';
import { addUserDesignOperation } from '~/redux/slices/undoStack';

interface ApplianceUserAction {
  appliance?: ApplianceModel;
  typeOfAppliance?: 'sink' | 'cooktop' | 'range' | 'customCutout';
}

export const useRegisterUserAction = () => {
  const dispatch = useAppDispatch();

  const registerCornerAction = useCallback(
    (cornerType: DiagonalCornerOptions | 'cornerRadius') => {
      switch (cornerType) {
        case 'cornerRadius':
          dispatch(
            addUserDesignOperation(UserDesignOperation.MODIFY_CORNER_RADIUS)
          );
          break;
        case DiagonalCornerOptions.INSIDE_CORNER:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_INSIDE_DIAGONAL_CORNER
            )
          );
          break;
        case DiagonalCornerOptions.OUTSIDE_CORNER:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_OUTSIDE_DIAGONAL_CORNER
            )
          );
          break;
        case DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER:
          dispatch(
            addUserDesignOperation(UserDesignOperation.REMOVE_DIAGONAL_CORNER)
          );
          break;
        default:
          break;
      }
    },
    [dispatch]
  );

  const registerCustomSideAction = useCallback(
    (customSideType: CustomSideType) => {
      switch (customSideType) {
        case CustomSideType.RADIUS_OUT:
          dispatch(
            addUserDesignOperation(UserDesignOperation.MODIFY_RADIUS_OUT)
          );
          break;
        case CustomSideType.BUMP_OUT:
          dispatch(addUserDesignOperation(UserDesignOperation.MODIFY_BUMP_OUT));
          break;
        case CustomSideType.REMOVE_CUSTOM_SIDE:
          dispatch(
            addUserDesignOperation(UserDesignOperation.REMOVE_CUSTOM_SIDE)
          );
          break;
        default:
          break;
      }
    },
    [dispatch]
  );

  const registerUserApplianceAction = useCallback(
    ({ appliance, typeOfAppliance }: ApplianceUserAction) => {
      if (!appliance && !typeOfAppliance) {
        return;
      }

      if (appliance) {
        switch (appliance.applianceType) {
          case 'RANGE':
            dispatch(addUserDesignOperation(UserDesignOperation.MODIFY_RANGE));
            break;
          case 'COOKTOP':
            dispatch(
              addUserDesignOperation(UserDesignOperation.MODIFY_COOKTOP)
            );
            break;
          case 'ELECTRICAL_CUTOUT':
            dispatch(
              addUserDesignOperation(
                appliance.consumerDesc.startsWith('Faucet')
                  ? UserDesignOperation.MODIFY_FAUCET
                  : UserDesignOperation.MODIFY_ELECTRICAL_CUTOUT
              )
            );
            break;
          default:
            break;
        }
      } else if (typeOfAppliance) {
        switch (typeOfAppliance) {
          case 'sink':
            dispatch(addUserDesignOperation(UserDesignOperation.MODIFY_SINK));
            break;
          case 'cooktop':
            dispatch(
              addUserDesignOperation(UserDesignOperation.MODIFY_COOKTOP)
            );
            break;
          case 'range':
            dispatch(addUserDesignOperation(UserDesignOperation.MODIFY_RANGE));
            break;
          case 'customCutout':
            dispatch(
              addUserDesignOperation(
                UserDesignOperation.MODIFY_ELECTRICAL_CUTOUT
              )
            );
            break;
          default:
            break;
        }
      }
    },
    [dispatch]
  );

  const registerSideItemAction = useCallback(
    (
      sideType:
        | SideItemType
        | 'backsplashWallSide'
        | 'backsplashHeight'
        | 'waterFallPanelHeight'
    ) => {
      switch (sideType) {
        case SideItemType.WALL_SIDE:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_SIDE_TYPE_WALL_SIDE
            )
          );
          break;
        case SideItemType.APPLIANCE_SIDE:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_SIDE_TYPE_APPLIANCE_SIDE
            )
          );
          break;
        case SideItemType.WATERFALL:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_SIDE_TYPE_WATERFALL_PANEL
            )
          );
          break;
        case 'backsplashWallSide':
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_BACKSPLASH_WALL_SIDE
            )
          );
          break;
        case 'backsplashHeight':
          dispatch(
            addUserDesignOperation(UserDesignOperation.MODIFY_BACKSPLASH_HEIGHT)
          );
          break;
        case 'waterFallPanelHeight':
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_WATERFALL_PANEL_HEIGHT
            )
          );
          break;
        default:
          break;
      }
    },
    [dispatch]
  );

  const registerUserAction = useCallback(
    (operation: UserDesignOperation) => {
      dispatch(addUserDesignOperation(operation));
    },
    [dispatch]
  );

  return {
    registerUserApplianceAction,
    registerCustomSideAction,
    registerCornerAction,
    registerUserAction,
    registerSideItemAction,
  };
};
